// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * How to use xCommon/update:
 *
 * 1. Using dispatch:
 *    Example:
 *    dispatch({
 *      type: "xCommon/update",
 *      data: { key: "isTabletScreen", value: true }
 *    });
 *    - The dispatched action includes "key" and "value" properties in the data object.
 *
 * 2. Using a handler function:
 *    Example:
 *    xCommonUpdate({ key: "isTabletScreen", value: true });
 *    - The xCommonUpdate handler simplifies the action creation for updating xCommon state.
 */

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { xCommonHandlerType } from "@steiner/App/Handlers/XNG/Common";
import { Draft } from "immer";

export type xCommonStateType = Readonly<{
    registerMenu: string[];
    unregisterMenu: string[];
    isSmallMobileScreen: boolean;
    isMobileScreen: boolean;
    isTabletScreen: boolean;
    isWideScreen: boolean;
}>;

export const initialCommon: xCommonStateType = {
    registerMenu: [],
    unregisterMenu: [],
    isSmallMobileScreen: false,
    isMobileScreen: false,
    isTabletScreen: false,
    isWideScreen: false,
};

const reducer = {
    "xCommon/update": (draft: Draft<xCommonStateType>, action: { data: xCommonHandlerType }) => {
        const { key, value } = action.data;
        draft[key] = value as never;
    },
    "xCommon/updateMultiple": (draft: Draft<xCommonStateType>, action: { data: xCommonHandlerType[] }) => {
        action.data.forEach(item => {
            const { key, value } = item;
            draft[key] = value as never;
        });
    },
};

const xCommon = createTypedReducerWithImmer(initialCommon, reducer);

(reducers as any).xCommon = xCommon;

export type xCommonState = {
    xCommon: xCommonStateType;
} & ApplicationState;

type xCommonReducers = {
    [key: string]: typeof xCommon;
};

export type xCommonActions = Parameters<xCommonReducers[keyof xCommonReducers]>[1] | AnyAction;
